import {
    addAdminUserRole,
    getAdminUserSummary,
    removeAdminUserRole,
    searchAdminUsers,
    type AdminUserSearchItem,
    type AdminUserSummary,
    type ElevatedRole
} from '@/api'
import axios from 'axios'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const elevatedRoles: ElevatedRole[] = ['Support', 'Admin', 'SuperAdmin']

function normalizeRoles(roles: string[] | undefined | null) {
    const normalized = new Set<string>(['User'])

    for (const role of roles ?? []) {
        if (role && ['User', ...elevatedRoles].includes(role as ElevatedRole | 'User')) {
            normalized.add(role)
        }
    }

    return ['User', ...elevatedRoles.filter((role) => normalized.has(role))]
}

export { type ElevatedRole }

export const useAdminStore = defineStore('admin', () => {
    const isIpValidated = ref<boolean | null>(null)
    const isChecking = ref(false)
    const lastDetectedIp = ref<string | null>(null)
    const allowedIps = ['81.7.136.98', '81.7.137.142']
    const currentRoles = ref<string[]>(['User'])
    const searchResults = ref<AdminUserSearchItem[]>([])
    const selectedUser = ref<AdminUserSummary | null>(null)
    const totalResults = ref(0)
    const isSearching = ref(false)
    const isLoadingUser = ref(false)
    const isMutatingRole = ref(false)
    const errorMessage = ref<string | null>(null)
    const lastCorrelationId = ref<string | null>(null)

    const hasAdminAccess = computed(() => elevatedRoles.some((role) => currentRoles.value.includes(role)))
    const canMutateRoles = computed(() => currentRoles.value.includes('Admin') || currentRoles.value.includes('SuperAdmin'))
    const isSuperAdmin = computed(() => currentRoles.value.includes('SuperAdmin'))

    async function validateIp() {
        if (isIpValidated.value === true) {
            return true
        }

        if (isChecking.value) {
            return false
        }

        isChecking.value = true
        try {
            const response = await fetch('https://api.ipify.org?format=json')
            const data = await response.json()
            const clientIp = data.ip
            lastDetectedIp.value = clientIp

            const isAllowed = allowedIps.includes(clientIp)
            isIpValidated.value = isAllowed
            return isAllowed
        } catch (error) {
            console.error('Failed to validate IP:', error)
            isIpValidated.value = true
            return true
        } finally {
            isChecking.value = false
        }
    }

    function setCurrentRoles(roles: string[] | undefined | null) {
        currentRoles.value = normalizeRoles(roles)
    }

    function canManageRole(targetRoles: string[] | undefined | null, role: ElevatedRole) {
        if (!canMutateRoles.value) {
            return false
        }

        if (isSuperAdmin.value) {
            return true
        }

        const elevatedTargetRoles = normalizeRoles(targetRoles).filter((targetRole) => targetRole !== 'User')
        return role === 'Support' && elevatedTargetRoles.every((targetRole) => targetRole === 'Support')
    }

    async function searchUsers(query: string) {
        isSearching.value = true
        errorMessage.value = null

        try {
            const result = await searchAdminUsers(query)
            searchResults.value = result.items
            totalResults.value = result.total
        } catch (error) {
            errorMessage.value = getErrorMessage(error, 'Kunne ikke soege brugere.')
        } finally {
            isSearching.value = false
        }
    }

    async function loadUserSummary(id: string) {
        isLoadingUser.value = true
        errorMessage.value = null

        try {
            selectedUser.value = await getAdminUserSummary(id)
        } catch (error) {
            errorMessage.value = getErrorMessage(error, 'Kunne ikke hente brugeroplysninger.')
        } finally {
            isLoadingUser.value = false
        }
    }

    async function addRole(id: string, role: ElevatedRole) {
        await mutateRole(id, role, addAdminUserRole)
    }

    async function removeRole(id: string, role: ElevatedRole) {
        await mutateRole(id, role, removeAdminUserRole)
    }

    async function mutateRole(
        id: string,
        role: ElevatedRole,
        action: (userId: string, userRole: ElevatedRole) => Promise<{ correlationId: string; user: AdminUserSummary }>
    ) {
        isMutatingRole.value = true
        errorMessage.value = null

        try {
            const response = await action(id, role)
            selectedUser.value = response.user
            lastCorrelationId.value = response.correlationId
            searchResults.value = searchResults.value.map((item) =>
                item.id === response.user.id
                    ? {
                        ...item,
                        roles: response.user.roles,
                        organizerCount: response.user.organizerCount,
                        name: response.user.name
                    }
                    : item
            )
        } catch (error) {
            errorMessage.value = getErrorMessage(error, 'Kunne ikke aendre rollen.')
        } finally {
            isMutatingRole.value = false
        }
    }

    function getErrorMessage(error: unknown, fallback: string) {
        if (axios.isAxiosError(error)) {
            return (error.response?.data?.error as string | undefined) ?? error.message ?? fallback
        }

        if (error instanceof Error) {
            return error.message
        }

        return fallback
    }

    return {
        isIpValidated,
        isChecking,
        lastDetectedIp,
        currentRoles,
        hasAdminAccess,
        canMutateRoles,
        searchResults,
        selectedUser,
        totalResults,
        isSearching,
        isLoadingUser,
        isMutatingRole,
        errorMessage,
        lastCorrelationId,
        validateIp,
        setCurrentRoles,
        canManageRole,
        searchUsers,
        loadUserSummary,
        addRole,
        removeRole
    }
})
